import { apiFetch } from '../../utils/apiClient';

/** Cache blob object URLs so audition + instant mix share one fetch per track. */
const blobUrlByTrackId = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

export function musicPreviewApiPath(musicId: string): string {
  const mid = String(musicId || '').trim();
  return `/api/rtts/music/${encodeURIComponent(mid)}/preview`;
}

/**
 * Map gain dB (−40…0) → HTMLMediaElement.volume (0…1).
 * Approximate amplitude: 10^(dB/20).
 */
export function gainDbToLinearVolume(db: number): number {
  const n = Number(db);
  if (!Number.isFinite(n)) return 0;
  const linear = 10 ** (n / 20);
  return Math.max(0, Math.min(1, linear));
}

/**
 * Fetch the session-gated preview stream and return a blob: URL.
 * Never logs the URL. Mock mode returns a silent WAV data URL.
 * Failures throw without console noise (callers catch for silent degrade).
 */
export async function fetchMusicPreviewObjectUrl(
  musicId: string,
  options?: { previewUrl?: string | null; useMock?: boolean }
): Promise<string> {
  const mid = String(musicId || '').trim();
  if (!mid) {
    throw new Error('music_id required');
  }
  const cached = blobUrlByTrackId.get(mid);
  if (cached) return cached;

  const pending = inflight.get(mid);
  if (pending) return pending;

  const work = (async () => {
    if (options?.useMock) {
      // Tiny silent WAV — enough for <audio> to load in tests/mock.
      const silent =
        'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';
      blobUrlByTrackId.set(mid, silent);
      return silent;
    }
    const raw = (options?.previewUrl || '').trim();
    const path =
      raw && raw.startsWith('/')
        ? raw
        : musicPreviewApiPath(mid);
    let response: Response;
    try {
      response = await apiFetch(path);
    } catch {
      throw new Error('music preview unavailable');
    }
    if (!response.ok) {
      throw new Error(`music preview failed: ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    blobUrlByTrackId.set(mid, objectUrl);
    return objectUrl;
  })().finally(() => {
    inflight.delete(mid);
  });

  inflight.set(mid, work);
  return work;
}

/** Test helper — clear blob cache between cases. */
export function clearMusicPreviewCache(): void {
  for (const url of blobUrlByTrackId.values()) {
    if (url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    }
  }
  blobUrlByTrackId.clear();
  inflight.clear();
}
